import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { loginSchema, type LoginFormData } from '@/schemas/auth';
import { useAuth } from '@/hooks/useAuth';
import { AuthLayout } from '@/layouts/AuthLayout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { isDemo } from '@/lib/env';
import type { ApiError } from '@/types/api';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [apiError, setApiError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setApiError('');
    try {
      await login(data.email, data.password);
      navigate('/', { replace: true });
    } catch (err) {
      setApiError((err as ApiError).error || 'Login failed');
    }
  };

  return (
    <AuthLayout title="PPE Tracker" subtitle="Sign in to your account">
      {isDemo() && (
        <div className="mb-4 rounded-md border border-accent-warning/30 bg-accent-warning/10 px-4 py-3 text-sm text-accent-warning">
          Demo mode — use <strong>admin@demofd.com</strong> / <strong>ChiefDemo26!</strong>
        </div>
      )}

      {apiError && (
        <div role="alert" className="mb-4 rounded-md border border-accent-danger/30 bg-accent-danger/10 px-4 py-3 text-sm text-accent-danger">
          {apiError}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          error={errors.email?.message}
          {...register('email')}
        />
        <Input
          label="Password"
          type="password"
          autoComplete="current-password"
          error={errors.password?.message}
          {...register('password')}
        />
        <Button type="submit" loading={isSubmitting} className="w-full">
          Sign In
        </Button>
      </form>

      <div className="mt-4 flex flex-col gap-2 text-center text-sm">
        <Link to="/forgot-password" className="text-accent-primary hover:underline">
          Forgot password?
        </Link>
        <span className="text-text-muted">
          Don&apos;t have an account?{' '}
          <Link to="/signup" className="text-accent-primary hover:underline">
            Sign up
          </Link>
        </span>
      </div>
    </AuthLayout>
  );
}
